import { COLORS, TYPOGRAPHY } from '@app/constants';
import { FC, useMemo } from 'react';
import styled, { css } from 'styled-components';
import Slider from '../Slider';

interface Props {
  minBorder: number;
  maxBorder: number;
  value: number;
  step?: number;
  className?: string;
  onChangeValue: (value: any) => void;
  borderLabel?: 'max' | 'min';
  textError?: string;
}

const SliderInput: FC<Props> = ({
  value,
  onChangeValue,
  minBorder,
  maxBorder,
  step = 1,
  className,
  borderLabel = 'max',
  textError,
}) => {
  const [isReverse, borderValue] = useMemo(() => {
    const isReverse = borderLabel === 'min';

    if (isReverse) return [isReverse, minBorder];

    return [isReverse, maxBorder];
  }, [borderLabel, maxBorder, minBorder]);

  return (
    <Root className={className}>
      <Labels $reversed={isReverse}>
        <Value>{value}</Value>
        <BorderValue>{borderValue}</BorderValue>
      </Labels>
      <StyledSlider
        value={value}
        onChange={onChangeValue}
        min={minBorder}
        max={maxBorder}
        step={step}
      />
      {textError && <TextError>{textError}</TextError>}
    </Root>
  );
};

const Root = styled.div`
  position: relative;
`;

const Labels = styled.div<{ $reversed?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 16px 10px 0;

  ${({ $reversed }) =>
    $reversed &&
    css`
      flex-direction: row-reverse;
    `}

  ${TYPOGRAPHY.body1Regular14};
`;

const Value = styled.span`
  color: ${COLORS.grayscale700};
`;

const StyledSlider = styled(Slider)`
  margin: 0 8px;

  .rc-slider {
    height: 3px;
  }
  .rc-slider-rail {
    height: 3px;
  }
  .rc-slider-track {
    top: 0;
    height: 3px;
    background-color: ${COLORS.primary300};
  }
  .rc-slider-handle {
    width: 16px;
    height: 16px;
    margin-top: -7px;
  }
`;

const BorderValue = styled.span`
  color: ${COLORS.grayscale300};
  pointer-events: none;
`;

const TextError = styled.p`
  ${TYPOGRAPHY.footnoteMedium13}
  color: ${COLORS.systemError};
  margin-top: 4px;
`;

export default SliderInput;
